<template>
    <div class="us-page">
        <!-- ────────── HEADER ────────── -->
        <header class="us-header">
            <div class="us-header__inner">
                <div class="us-header__title-wrap">
                    <h1 class="us-title">משתמשים</h1>
                    <span class="us-total-pill" :title="`סה״כ ${users.length} משתמשים`">
                        <v-icon size="14">mdi-account-group-outline</v-icon>
                        <span>{{ users.length.toLocaleString('he-IL') }}</span>
                    </span>
                </div>
                <button type="button" class="us-cta" @click="openCreateDIalog()">
                    <v-icon size="17">mdi-account-plus-outline</v-icon>
                    <span>הוסף משתמש</span>
                </button>
            </div>
        </header>

        <!-- ────────── MAIN ────────── -->
        <main class="us-main">

            <!-- ROLE BREAKDOWN STATS -->
            <section class="us-stats" aria-label="פילוח לפי תפקיד">
                <div class="us-stat us-stat--all">
                    <div class="us-stat__icon"><v-icon size="19">mdi-account-group-outline</v-icon></div>
                    <div class="us-stat__body">
                        <span class="us-stat__label">סה״כ</span>
                        <strong class="us-stat__value">{{ users.length.toLocaleString('he-IL') }}</strong>
                    </div>
                </div>
                <div class="us-stat us-stat--admin">
                    <div class="us-stat__icon"><v-icon size="19">mdi-shield-crown-outline</v-icon></div>
                    <div class="us-stat__body">
                        <span class="us-stat__label">מנהלים</span>
                        <strong class="us-stat__value">{{ countByRole('מנהל').toLocaleString('he-IL') }}</strong>
                    </div>
                </div>
                <div class="us-stat us-stat--agent">
                    <div class="us-stat__icon"><v-icon size="19">mdi-account-tie-outline</v-icon></div>
                    <div class="us-stat__body">
                        <span class="us-stat__label">סוכנים</span>
                        <strong class="us-stat__value">{{ countByRole('סוכן').toLocaleString('he-IL') }}</strong>
                    </div>
                </div>
                <div class="us-stat us-stat--seller">
                    <div class="us-stat__icon"><v-icon size="19">mdi-storefront-outline</v-icon></div>
                    <div class="us-stat__body">
                        <span class="us-stat__label">משווקים</span>
                        <strong class="us-stat__value">{{ countByRole('משווק').toLocaleString('he-IL') }}</strong>
                    </div>
                </div>
            </section>

            <!-- TOOLBAR -->
            <div class="us-toolbar">
                <div class="us-search">
                    <v-icon class="us-search__icon" size="19">mdi-magnify</v-icon>
                    <input v-model="search" type="text" class="us-search__input"
                        placeholder="חיפוש לפי שם, ת״ז, טלפון, חנות או תפקיד…" />
                    <button v-if="search" type="button" class="us-search__clear" @click="search = ''"
                        aria-label="נקה חיפוש">
                        <v-icon size="14">mdi-close</v-icon>
                    </button>
                </div>
                <div class="us-pagesize">
                    <label for="us-pagesize-select">שורות</label>
                    <select id="us-pagesize-select" class="us-pagesize__select" v-model.number="itemsPerPage">
                        <option v-for="n in [20, 50, 100, 500]" :key="n" :value="n">{{ n }}</option>
                    </select>
                </div>
            </div>

            <!-- TABLE -->
            <div class="us-table-card">
                <v-data-table id="table1" :headers="headers" :items="users" hide-default-footer :search="search"
                    :page.sync="currentPage" :items-per-page="itemsPerPage" @page-count="pageCount = $event"
                    class="us-table">

                    <template v-for="header in headers" v-slot:[`header.${header.value}`]="{ header }">
                        <span class="us-th">{{ header.text }}</span>
                    </template>

                    <template v-slot:item.index="{ index }">
                        <span class="us-index">{{ (currentPage - 1) * itemsPerPage + index + 1 }}</span>
                    </template>

                    <template v-slot:item.name="{ item }">
                        <div class="us-user-cell">
                            <span class="us-avatar" :class="`us-avatar--${roleKey(item.role)}`"
                                :title="item.role">{{ initial(item.name) }}</span>
                            <span class="us-user-name">{{ item.name }}</span>
                        </div>
                    </template>

                    <template v-slot:item.phone="{ item }">
                        <span class="us-num-cell">{{ item.phone || '—' }}</span>
                    </template>

                    <template v-slot:item.card_id="{ item }">
                        <span class="us-num-cell">{{ item.card_id || '—' }}</span>
                    </template>

                    <template v-slot:item.shop_name="{ item }">
                        <span :class="['us-cell', { 'us-muted': !item.shop_name }]">{{ item.shop_name || '—' }}</span>
                    </template>

                    <template v-slot:item.shop_address="{ item }">
                        <span :class="['us-cell', { 'us-muted': !item.shop_address }]">{{ item.shop_address || '—' }}</span>
                    </template>

                    <template v-slot:item.status="{ item }">
                        <span class="us-status-pill"
                            :class="`us-status-pill--${item.status === 'פעיל' ? 'active' : 'inactive'}`">
                            <span class="us-status-pill__dot"></span>
                            {{ item.status }}
                        </span>
                    </template>

                    <template v-slot:item.role="{ item }">
                        <span class="us-role-chip" :class="`us-role-chip--${roleKey(item.role)}`">
                            <v-icon size="13">{{ roleIcon(item.role) }}</v-icon>
                            {{ item.role }}
                        </span>
                    </template>

                    <template v-slot:item.agent_id.name="{ item }">
                        <span v-if="item.agent_id && item.agent_id.name" class="us-agent-link">
                            <v-icon size="13">mdi-link-variant</v-icon>
                            {{ item.agent_id.name }}
                        </span>
                        <span v-else class="us-muted">—</span>
                    </template>

                    <template v-slot:item.actions="{ item, index }">
                        <div class="us-actions">
                            <v-tooltip bottom v-if="item.role !== 'מנהל'">
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="us-action-btn us-action-btn--enter"
                                        @click="switchToUser(item)" aria-label="כניסה למערכת כמשתמש">
                                        <v-icon size="17">mdi-login-variant</v-icon>
                                    </button>
                                </template>
                                <span>כניסה למערכת</span>
                            </v-tooltip>
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="us-action-btn us-action-btn--edit"
                                        @click="openEditDialog(item, index)" aria-label="עריכה">
                                        <v-icon size="17">mdi-pencil-outline</v-icon>
                                    </button>
                                </template>
                                <span>עריכה</span>
                            </v-tooltip>
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="us-action-btn us-action-btn--delete"
                                        @click="openDeleteDialog(item, index)" aria-label="מחיקה">
                                        <v-icon size="17">mdi-delete-outline</v-icon>
                                    </button>
                                </template>
                                <span>מחיקה</span>
                            </v-tooltip>
                        </div>
                    </template>

                    <template v-slot:no-data>
                        <div class="us-empty">
                            <v-progress-linear v-if="progressShow" indeterminate color="#8066f0" height="3"
                                class="us-empty__progress"></v-progress-linear>
                            <template v-else>
                                <div class="us-empty__icon"><v-icon size="30">mdi-account-search-outline</v-icon></div>
                                <h3 class="us-empty__title">לא נמצאו משתמשים</h3>
                                <p class="us-empty__hint">נסה לרענן או לשנות את החיפוש</p>
                                <button type="button" class="us-empty__btn" @click="getUsers()">רענון</button>
                            </template>
                        </div>
                    </template>

                    <template v-slot:no-results>
                        <div class="us-empty">
                            <div class="us-empty__icon"><v-icon size="30">mdi-magnify-close</v-icon></div>
                            <h3 class="us-empty__title">לא נמצאו תוצאות</h3>
                            <p class="us-empty__hint">נסה מילות חיפוש אחרות</p>
                        </div>
                    </template>
                </v-data-table>

                <div v-if="pageCount > 1" class="us-pagination-wrap">
                    <v-pagination v-model="currentPage" color="#8066f0" circle :length="pageCount"
                        :total-visible="7"></v-pagination>
                </div>
            </div>
        </main>

        <template>
            <v-overlay :value="overlay">
                <v-progress-circular indeterminate size="84" color="#0d2c6d"></v-progress-circular>
            </v-overlay>
        </template>

        <AddEditDialog v-model="addEditDialog" v-if="addEditDialog" :formTitle="formTitle" :itemToEdit="editedItem"
            @userAdded="newUserSaved" @userEdited="editedUserSaved"></AddEditDialog>

        <AlertDialog v-model="alertDialog" v-if="alertDialog" :item="itemToDelete" @userDeleted="userDeleted">
        </AlertDialog>

        <SnackBar v-model="snackbar" :snacbarText="snacbarText" :snackbarColorBt="snackbarColorBt"></SnackBar>
    </div>
</template>
    
<script>

import SnackBar from '@/components/widgets/snackBar.vue'
import ApiServices from '@/services/api.service'
import AddEditDialog from '@/components/users/dialogs/AddEditDialog'
import AlertDialog from '@/components/users/dialogs/AlertDialog'
import Auth from '@/services/auth.service.js'

export default {

    components: {
        SnackBar,
        AddEditDialog,
        AlertDialog,
        
    },
    data: () => ({
        headers: [
            { text: '#', value: 'index', align: "center" },
            { text: 'שם', value: 'name', align: "center" },
            { text: 'טלפון', value: 'phone', align: "center" },
            { text: 'ת.ז/ח.פ', value: 'card_id', align: "center" },
            { text: 'שם החנות', value: 'shop_name', align: "center" },
            { text: 'מיקום החנות', value: 'shop_address', align: "center" },
            { text: 'סטטוס', value: 'status', align: "center" },
            { text: 'הרשאות', value: 'role', align: "center" },
            { text: 'משוייך לסוכן', value: 'agent_id.name', align: "center" },
            { text: 'פעולות', value: 'actions', align: "center" },
        ],
        overlay: false,
        alertDialog: false,
        addEditDialog: false,
        formTitle: "",
        users: [],
        editedItem: {},
        itemToDelete: {},
        snackbar: false,
        snackbarColorBt: "green",
        snacbarText: "",
        search: "",
        progressShow: false,
        currentPage: 1,
        pageCount: 0,
        itemsPerPage: 20,
    }),
    methods: {
        showSnackBar(message, color) {
            this.snacbarText = message;
            this.snackbarColorBt = color;
            this.snackbar = true
        },
        async getUsers() {
            try {
                this.progressShow = true;
                let token = localStorage.getItem("token");
                let api = process.env.VUE_APP_BASE_URL + `/users/get_users`;
                const res = await fetch(api, ApiServices.requestOptions("GET", "", token));
                const jsonObject = await res.json();
                this.progressShow = false;
                if (res.status >= 400) {
                    this.showSnackBar("שגיאה בקבלת נתונים", "red");
                } else if (res.status === 200) {
                    this.users = jsonObject;
                    this.formatDetails();
                }
            } catch (error) {
                this.progressShow = false;
                this.showSnackBar("Error get files: " + error, "red");
            }
        },
        formatDetails() {
            this.users.forEach(user => {
                // user.createdAt = Utils.formatDateString(call.createdAt)
                user.status = user.status ? "פעיל" : "לא פעיל";
                user.role = user.role === "admin" ? "מנהל" : user.role === "agent" ? "סוכן" : "משווק";
            });
        },
        openEditDialog(item, index) {
            const originalItem = this.users.find(user => user._id === item._id);
            this.editedItem = originalItem;
            this.formTitle = "ערוך משתמש"
            this.addEditDialog = true
        },

        openDeleteDialog(item, index) {
            this.itemToDelete = item
            this.formTitle = "האם אתה בטוח שברצונך למחוק את המשתמש?"
            this.alertDialog = true
        },
        openCreateDIalog() {
            this.formTitle = "הוסף משתמש"
            this.editedItem = {}
            this.addEditDialog = true
        },
        newUserSaved(item, text, color) {

            this.showSnackBar(text, color)
            if (item === "") {
                return
            }
            this.users.push(item);

        },
        editedUserSaved(item, text, color) {
            this.showSnackBar(text, color)
            if (item === "") {
                return
            }
            const index = this.users.findIndex(user => user._id === item._id);
            item.status = item.status ? "פעיל" : "לא פעיל";
            item.role = item.role === "admin" ? "מנהל" : item.role === "agent" ? "סוכן" : "משווק";
            Object.assign(this.users[index], item);
        },
        userDeleted(item, text, color) {
            if (!item) {
                this.showSnackBar(text, color)
                return
            }
            const index = this.users.findIndex(user => user._id === item._id);
            this.users.splice(index, 1);
            this.showSnackBar(text, color)
        },
        async switchToUser(item) {
            this.progressShow = true
            let token = localStorage.getItem("token");
            const role = this.getUserRole();
            localStorage.removeItem("logo");
            if (role === "admin") {
                localStorage.setItem('tokenAdmin', token);
            } else if (role === "agent") {
                localStorage.setItem('tokenAgent', token);
            } else { return }
            let MyJSON = JSON.stringify({ _id: item._id });
            let api = process.env.VUE_APP_BASE_URL + "/users/swich_to_user"

            try {
                const response = await fetch(api, ApiServices.requestOptions("POST", MyJSON, token));
                this.progressShow = false;
                const jsonObject = await response.json();
                if (response.status >= 400) {
                    this.showSnackBar("שגיאת התחברות: " + jsonObject.message, "red");
                } else if (response.status === 200) {
                    localStorage.setItem('token', jsonObject.token);
                    this.$store.commit('setRenderNav');
                    this.$router.replace({ name: "home" })
                }
            } catch (error) {
                this.showSnackBar("Error Login: " + error, "red");
            }
        },
        getUserRole() {
            return Auth.getUserRole()
        },
        // ── Presentation helpers (no side effects, pure functions for template) ──
        initial(name) {
            const s = (name || '').trim();
            return s ? s.charAt(0).toUpperCase() : '?';
        },
        roleKey(roleHebrew) {
            if (roleHebrew === 'מנהל') return 'admin';
            if (roleHebrew === 'סוכן') return 'agent';
            return 'seller';
        },
        roleIcon(roleHebrew) {
            if (roleHebrew === 'מנהל') return 'mdi-shield-crown-outline';
            if (roleHebrew === 'סוכן') return 'mdi-account-tie-outline';
            return 'mdi-storefront-outline';
        },
        countByRole(roleHebrew) {
            return this.users.filter(u => u.role === roleHebrew).length;
        }
    },
    created() {
        this.getUsers();
    },
}
</script>
<style scoped>
/* ─────────────────────── TOKENS ─────────────────────── */
.us-page {
    --u-navy: #0d2c6d;
    --u-navy-soft: #142f78;
    --u-indigo: #3F51B5;
    --u-indigo-soft: rgba(63, 81, 181, 0.10);
    --u-purple: #8066f0;
    --u-purple-soft: rgba(128, 102, 240, 0.10);
    --u-purple-deep: #6b54e0;
    --u-gold: #fdb721;
    --u-bg: #f4f5fa;          /* slightly purple-tinted bg, distinct from other pages */
    --u-surface: #ffffff;
    --u-border: #e3e8ef;
    --u-border-soft: #eef2f7;
    --u-text: #0f172a;
    --u-text-soft: #475569;
    --u-text-mute: #94a3b8;
    --u-green: #16804b;
    --u-green-soft: rgba(22, 128, 75, 0.10);
    --u-red: #c74747;
    --u-red-soft: rgba(199, 71, 71, 0.10);

    --u-shadow-sm: 0 1px 2px rgba(13, 44, 109, 0.04), 0 1px 3px rgba(13, 44, 109, 0.06);
    --u-shadow-md: 0 4px 14px rgba(13, 44, 109, 0.07), 0 2px 4px rgba(13, 44, 109, 0.04);
    --u-shadow-lg: 0 18px 40px -12px rgba(128, 102, 240, 0.22), 0 8px 16px -8px rgba(13, 44, 109, 0.10);
    --u-ease: cubic-bezier(0.22, 1, 0.36, 1);

    min-height: 100vh;
    padding-bottom: 60px;
    background: var(--u-bg);
    direction: rtl;
    font-family: 'Assistant', 'Heebo', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

/* ─────────────────────── HEADER ─────────────────────── */
/* Note: purple flourish (vs gold in other pages) — visual marker that this is Users */
.us-header {
    position: relative;
    background:
        radial-gradient(800px 200px at 90% -30%, rgba(128, 102, 240, 0.22), transparent 60%),
        linear-gradient(135deg, #0d2c6d 0%, #1a2570 55%, #2a2a8a 100%);
    color: #fff;
    padding: 12px 0 14px;
    box-shadow: var(--u-shadow-md);
}

.us-header__inner {
    max-width: 1500px;
    margin: 0 auto;
    padding: 0 28px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}

.us-header__title-wrap {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
}

.us-title {
    margin: 0;
    font-size: 1.3rem;
    font-weight: 800;
    letter-spacing: -0.01em;
    line-height: 1.2;
}

.us-total-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.10);
    border: 1px solid rgba(255, 255, 255, 0.20);
    color: #fff;
    font-size: 0.85rem;
    font-weight: 700;
    font-feature-settings: "tnum" 1;
}
.us-total-pill ::v-deep .v-icon { color: rgba(255, 255, 255, 0.85) !important; }

/* Primary CTA in the header (replaces the old black button) */
.us-cta {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 7px 16px;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.30);
    background: rgba(128, 102, 240, 0.55);
    color: #fff;
    font-family: inherit;
    font-size: 0.9rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.22s var(--u-ease);
    backdrop-filter: blur(6px);
    flex-shrink: 0;
}
.us-cta:hover {
    background: var(--u-purple);
    border-color: rgba(255, 255, 255, 0.45);
    transform: translateY(-1px);
    box-shadow: 0 6px 14px -4px rgba(128, 102, 240, 0.55);
}
.us-cta ::v-deep .v-icon { color: #fff !important; }

/* ─────────────────────── MAIN + STATS ─────────────────────── */
.us-main {
    max-width: 1500px;
    margin: 22px auto 0;
    padding: 0 22px;
}

.us-stats {
    display: grid;
    grid-template-columns: repeat(4, minmax(150px, 1fr));
    gap: 12px;
    margin-bottom: 18px;
}

.us-stat {
    display: flex;
    align-items: center;
    gap: 11px;
    min-height: 70px;
    padding: 12px 14px;
    border-radius: 14px;
    background: var(--u-surface);
    border: 1px solid var(--u-border-soft);
    box-shadow: var(--u-shadow-md);
    animation: us-cardIn 0.45s var(--u-ease) backwards;
}
.us-stat:nth-child(1) { animation-delay: 0.00s; }
.us-stat:nth-child(2) { animation-delay: 0.06s; }
.us-stat:nth-child(3) { animation-delay: 0.12s; }
.us-stat:nth-child(4) { animation-delay: 0.18s; }

@keyframes us-cardIn {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
}

.us-stat__icon {
    width: 40px; height: 40px;
    flex-shrink: 0;
    display: grid; place-items: center;
    border-radius: 11px;
}
.us-stat__body { display: flex; flex-direction: column; gap: 1px; }
.us-stat__label {
    color: var(--u-text-mute);
    font-size: 0.76rem;
    font-weight: 700;
}
.us-stat__value {
    color: var(--u-text);
    font-size: 1.3rem;
    font-weight: 800;
    line-height: 1.05;
    letter-spacing: -0.01em;
    font-feature-settings: "tnum" 1;
}

.us-stat--all     .us-stat__icon { color: var(--u-purple); background: var(--u-purple-soft); }
.us-stat--admin   .us-stat__icon { color: var(--u-navy);   background: rgba(13, 44, 109, 0.09); }
.us-stat--agent   .us-stat__icon { color: var(--u-purple); background: var(--u-purple-soft); }
.us-stat--seller  .us-stat__icon { color: var(--u-indigo); background: var(--u-indigo-soft); }

/* ─────────────────────── TOOLBAR ─────────────────────── */
.us-toolbar {
    display: grid;
    grid-template-columns: minmax(260px, 1fr) auto;
    align-items: center;
    gap: 14px;
    margin-bottom: 14px;
}

.us-search {
    position: relative;
    display: flex;
    align-items: center;
    height: 42px;
    padding: 0 12px;
    border-radius: 12px;
    background: var(--u-surface);
    border: 1px solid var(--u-border);
    transition: border-color 0.2s var(--u-ease), box-shadow 0.2s var(--u-ease);
}
/* Purple focus ring (distinct from navy in other pages) */
.us-search:focus-within {
    border-color: var(--u-purple);
    box-shadow: 0 0 0 4px rgba(128, 102, 240, 0.12);
}
.us-search__icon { color: var(--u-text-mute) !important; }
.us-search__input {
    flex: 1;
    min-width: 0;
    padding: 0 9px;
    border: none;
    outline: none;
    background: transparent;
    color: var(--u-text);
    font-family: inherit;
    font-size: 0.91rem;
}
.us-search__input::placeholder { color: var(--u-text-mute); }
.us-search__clear {
    width: 22px; height: 22px;
    display: grid; place-items: center;
    border: none;
    border-radius: 50%;
    background: var(--u-border-soft);
    color: var(--u-text-soft);
    cursor: pointer;
}

.us-pagesize {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--u-text-soft);
    font-size: 0.84rem;
    font-weight: 700;
}
.us-pagesize__select {
    padding: 7px 12px;
    border: 1px solid var(--u-border);
    border-radius: 10px;
    background: var(--u-surface);
    color: var(--u-text);
    font-family: inherit;
    font-size: 0.87rem;
    font-weight: 700;
    cursor: pointer;
    outline: none;
}
.us-pagesize__select:focus {
    border-color: var(--u-purple);
    box-shadow: 0 0 0 4px rgba(128, 102, 240, 0.12);
}

/* ─────────────────────── TABLE ─────────────────────── */
.us-table-card {
    overflow: hidden;
    border: 1px solid var(--u-border-soft);
    border-radius: 18px;
    background: var(--u-surface);
    box-shadow: var(--u-shadow-md);
}

::v-deep #table1.v-data-table {
    background: transparent !important;
}
::v-deep #table1.v-data-table .v-data-table-header th {
    height: 52px !important;
    border-bottom: 1px solid var(--u-border-soft) !important;
    border-left: none !important;
    background: #fafbff !important;
    text-align: center !important;
}
::v-deep #table1.v-data-table .v-data-table-header th .v-data-table-header__icon {
    color: var(--u-text-mute) !important;
    opacity: 1 !important;
}

.us-th {
    color: var(--u-text-soft) !important;
    font-family: inherit !important;
    font-size: 0.78rem !important;
    font-weight: 800 !important;
    letter-spacing: 0.02em;
    text-transform: uppercase;
}

::v-deep #table1.v-data-table tbody tr {
    transition: background 0.18s var(--u-ease);
}
::v-deep #table1.v-data-table tbody tr:hover {
    background: #fafbff !important;
}
::v-deep #table1.v-data-table tbody tr td {
    height: 64px;
    padding: 11px 12px !important;
    border-bottom: 1px solid var(--u-border-soft) !important;
    color: var(--u-text);
    font-family: inherit;
    font-size: 0.88rem;
    text-align: center;
}

.us-index {
    color: var(--u-text-mute);
    font-size: 0.82rem;
    font-weight: 700;
    font-feature-settings: "tnum" 1;
}
.us-num-cell {
    direction: ltr;
    display: inline-block;
    color: var(--u-text);
    font-feature-settings: "tnum" 1;
    font-weight: 600;
}
.us-cell { color: var(--u-text); }
.us-muted { color: var(--u-text-mute); }

/* ─────────────────────── USER CELL (name + avatar) ─────────────────────── */
.us-user-cell {
    display: flex;
    align-items: center;
    gap: 10px;
    justify-content: flex-start;
    min-width: 0;
    padding-right: 4px;
}
.us-avatar {
    flex-shrink: 0;
    width: 36px; height: 36px;
    border-radius: 50%;
    display: grid; place-items: center;
    color: #fff;
    font-weight: 800;
    font-size: 0.88rem;
    letter-spacing: 0.02em;
    user-select: none;
    box-shadow: 0 2px 6px -2px rgba(13, 44, 109, 0.30);
}
.us-avatar--admin  { background: linear-gradient(135deg, #0d2c6d 0%, #1f3a8a 100%); }
.us-avatar--agent  { background: linear-gradient(135deg, #8066f0 0%, #6b54e0 100%); }
.us-avatar--seller { background: linear-gradient(135deg, #3F51B5 0%, #5263c4 100%); }

.us-user-name {
    font-weight: 700;
    color: var(--u-text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* ─────────────────────── ROLE CHIP ─────────────────────── */
.us-role-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 0.76rem;
    font-weight: 800;
    border: 1px solid transparent;
}
.us-role-chip ::v-deep .v-icon { color: inherit !important; }
.us-role-chip--admin  { color: var(--u-navy);   background: rgba(13, 44, 109, 0.08); border-color: rgba(13, 44, 109, 0.15); }
.us-role-chip--agent  { color: var(--u-purple-deep); background: var(--u-purple-soft); border-color: rgba(128, 102, 240, 0.22); }
.us-role-chip--seller { color: var(--u-indigo); background: var(--u-indigo-soft); border-color: rgba(63, 81, 181, 0.20); }

/* ─────────────────────── STATUS PILL ─────────────────────── */
.us-status-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 0.76rem;
    font-weight: 800;
}
.us-status-pill__dot {
    width: 6px; height: 6px;
    border-radius: 50%;
    background: currentColor;
}
.us-status-pill--active   { color: var(--u-green); background: var(--u-green-soft); }
.us-status-pill--inactive { color: var(--u-red);   background: var(--u-red-soft); }

/* ─────────────────────── AGENT LINK ─────────────────────── */
.us-agent-link {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: var(--u-text-soft);
    font-size: 0.85rem;
    font-weight: 600;
}
.us-agent-link ::v-deep .v-icon { color: var(--u-purple) !important; }

/* ─────────────────────── ACTIONS ─────────────────────── */
.us-actions {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    justify-content: center;
}
.us-action-btn {
    width: 32px; height: 32px;
    display: grid; place-items: center;
    border: 1px solid var(--u-border);
    border-radius: 9px;
    background: var(--u-surface);
    color: var(--u-text-soft);
    cursor: pointer;
    transition: all 0.18s var(--u-ease);
}
.us-action-btn:hover {
    border-color: transparent;
    color: #fff;
    transform: translateY(-1px);
}
/* Sign-in-as: special gold treatment — it's a powerful admin-only action */
.us-action-btn--enter:hover {
    background: linear-gradient(135deg, #f0a30e 0%, var(--u-gold) 100%);
    color: #2d1f00;
    box-shadow: 0 5px 12px -4px rgba(253, 183, 33, 0.55);
}
/* Edit: purple (this page's accent) */
.us-action-btn--edit:hover {
    background: var(--u-purple);
    box-shadow: 0 5px 12px -4px rgba(128, 102, 240, 0.55);
}
/* Delete: red */
.us-action-btn--delete:hover {
    background: var(--u-red);
    box-shadow: 0 5px 12px -4px rgba(199, 71, 71, 0.55);
}

/* ─────────────────────── EMPTY ─────────────────────── */
.us-empty {
    padding: 50px 20px;
    text-align: center;
}
.us-empty__progress { max-width: 240px; margin: 0 auto; border-radius: 999px; }
.us-empty__icon {
    width: 64px; height: 64px;
    margin: 0 auto 14px;
    display: grid; place-items: center;
    border-radius: 50%;
    color: var(--u-purple);
    background: var(--u-purple-soft);
}
.us-empty__title {
    margin: 0 0 4px;
    color: var(--u-text);
    font-size: 1.05rem;
    font-weight: 700;
}
.us-empty__hint {
    margin: 0 0 16px;
    color: var(--u-text-mute);
    font-size: 0.88rem;
}
.us-empty__btn {
    padding: 10px 26px;
    border: none;
    border-radius: 10px;
    background: var(--u-purple);
    color: #fff;
    font-family: inherit;
    font-weight: 700;
    font-size: 0.9rem;
    cursor: pointer;
    box-shadow: 0 6px 14px -4px rgba(128, 102, 240, 0.45);
    transition: all 0.2s var(--u-ease);
}
.us-empty__btn:hover { transform: translateY(-1px); filter: brightness(1.08); }

/* ─────────────────────── PAGINATION ─────────────────────── */
.us-pagination-wrap {
    display: flex;
    justify-content: center;
    padding: 17px 12px 12px;
    border-top: 1px solid var(--u-border-soft);
}
::v-deep .us-pagination-wrap .v-pagination__item,
::v-deep .us-pagination-wrap .v-pagination__navigation {
    border: 1px solid var(--u-border) !important;
    box-shadow: none !important;
    color: var(--u-text-soft) !important;
}
::v-deep .us-pagination-wrap .v-pagination__item--active {
    border-color: var(--u-purple) !important;
    background: var(--u-purple) !important;
    color: #fff !important;
    box-shadow: 0 4px 10px -2px rgba(128, 102, 240, 0.45) !important;
}

/* ─────────────────────── RESPONSIVE ─────────────────────── */
@media (max-width: 900px) {
    .us-header__inner { padding: 0 18px; flex-wrap: wrap; gap: 10px; }
    .us-title { font-size: 1.1rem; }
    .us-stats { grid-template-columns: repeat(2, minmax(140px, 1fr)); }
    .us-main { padding: 0 12px; margin-top: 16px; }
    .us-toolbar { grid-template-columns: 1fr; }
    .us-pagesize { justify-content: flex-end; }
}

@media (max-width: 480px) {
    .us-cta span { display: none; }
    .us-cta { padding: 7px 9px; }
    ::v-deep #table1.v-data-table tbody tr td { padding: 9px 6px !important; font-size: 0.82rem; }
}

@media (prefers-reduced-motion: reduce) {
    .us-stat { animation: none; }
}
</style>